import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service';
import { AuthService } from '../services/auth.service';
import { httpErrorMessage } from '../utils/http-error';
import {
  isSessionExpiryHandled,
  markSessionExpiryHandled,
} from './session-expiry';

/**
 * HTTP Interceptor for handling errors globally
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const authService = inject(AuthService);

  // Endpoints that should not trigger redirect on 401
  // These are auth endpoints where 401 is expected/normal
  const silentAuthEndpoints = [
    '/auth/refresh',
    '/auth/login',
    '/auth/register',
    '/auth/reactivate',
    '/auth/check-user',
    '/auth/logout'
  ];

  const isSilentAuthEndpoint = silentAuthEndpoints.some(endpoint => req.url.includes(endpoint));
  const skipErrorNotification = req.headers.has('X-Skip-Error-Notification');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An error occurred';
      let showNotification = !skipErrorNotification;

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
      } else {
        // Server-side error
        switch (error.status) {
          case 401:
            // Don't redirect or show error for silent auth endpoints
            // These are expected to fail for unauthenticated users
            if (isSilentAuthEndpoint) {
              // Silently fail - let the calling code handle it
              return throwError(() => error);
            }

            if (isSessionExpiryHandled()) {
              showNotification = false;
              return throwError(() => error);
            }
            markSessionExpiryHandled();

            errorMessage = 'Session expired. Please login again.';
            const currentUrl = router.url || window.location.pathname;
            if (!currentUrl.includes('/auth/')) {
              authService.clearLocalSession();
            } else {
              showNotification = false;
            }
            break;
          case 403:
            if (isOrganizationSuspended(error)) {
              errorMessage = 'Your organization has been suspended.';
              authService.clearLocalSession();
              if (!router.url.includes('/organization-suspended')) {
                router.navigate(['/organization-suspended']);
              }
              showNotification = false;
              break;
            }
            errorMessage = httpErrorMessage(error, 'Access forbidden.');
            break;
          case 404:
            // Don't show notification for 404s - often expected
            showNotification = false;
            break;
          case 429:
            errorMessage = 'Too many requests. Please try again later.';
            break;
          case 500:
            errorMessage = 'Internal server error. Please try again later.';
            break;
          default:
            errorMessage = error.error?.detail || error.message || 'An error occurred';
        }
      }

      // Show error notification (unless suppressed)
      if (showNotification) {
        notificationService.showError(errorMessage);
      }

      // Log error for debugging
      console.error('HTTP Error:', {
        status: error.status,
        message: errorMessage,
        url: req.url,
        error: error.error
      });

      return throwError(() => error);
    })
  );
};

function isOrganizationSuspended(error: HttpErrorResponse): boolean {
  const detail = error.error?.detail;
  if (detail && typeof detail === 'object' && detail.code === 'ORG_SUSPENDED') {
    return true;
  }
  const message = typeof detail === 'string' ? detail : detail?.message;
  return typeof message === 'string' && message.toLowerCase().includes('suspended');
}
